import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const plans = [
  {
    name: 'Individual',
    price: 'Free',
    period: 'stays free',
    description: 'For one person getting a workspace into use.',
    cta: 'Create a free account',
    href: 'https://app.synqtag.com/auth/register',
    featured: false,
    features: [
      'Up to 2 workspaces',
      'Up to 5 dimensions',
      'Unlimited work items',
      '1 user',
      'Board and task management',
    ],
  },
  {
    name: 'Team',
    price: '$9.95',
    period: 'per person / month',
    description: 'For a group that needs more than one login.',
    cta: 'Start on Team',
    href: 'https://app.synqtag.com/auth/register',
    featured: true,
    features: [
      'Unlimited workspaces',
      'Unlimited dimensions',
      'Unlimited members',
      'Time tracking and analytics',
      'Workflow automation',
      'Priority support',
    ],
  },
];

const rows: { name: string; individual: string; team: string }[] = [
  { name: 'Work items', individual: 'Unlimited', team: 'Unlimited' },
  { name: 'Workspaces', individual: '2', team: 'Unlimited' },
  { name: 'Dimensions', individual: '5', team: 'Unlimited' },
  { name: 'People', individual: '1', team: 'Unlimited, billed per person' },
  { name: 'Board, list, timeline, calendar, workload', individual: 'Yes', team: 'Yes' },
  { name: 'Time tracking and analytics', individual: '—', team: 'Yes' },
  { name: 'Workflow automation', individual: '—', team: 'Yes' },
  { name: 'Priority support', individual: '—', team: 'Yes' },
];

const faqs = [
  {
    q: 'Is the free plan a trial?',
    a: 'No. Individual is free for one person, with the limits above. Team is a paid monthly subscription. There is no separate 14-day trial.',
  },
  {
    q: 'How is Team billed?',
    a: 'Team is $9.95 per person each month, in US dollars, through Stripe. You start checkout from organization settings. An existing subscription is managed in the billing portal.',
  },
  {
    q: 'Can I cancel?',
    a: 'Yes, from the billing portal. Stripe applies the cancellation to the subscription. Keep a copy of anything you need before you close an account.',
  },
  {
    q: 'What about annual billing, PayPal, or an enterprise tier?',
    a: 'The product sells Individual and Team. Annual pricing, PayPal, and a separate enterprise contract are not part of checkout.',
  },
  {
    q: 'Do you have SSO, a native mobile app, or Slack built in?',
    a: 'No. Sign-in can ask for a code sent to your email. Connections to other tools go through API keys and webhooks, including Zapier, Make, and n8n.',
  },
];

export const Pricing: React.FC = () => {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="pb-20 pt-28">
      <div className="container-width section-padding">
        <p className="eyebrow">Pricing</p>
        <h1 className="max-w-3xl font-display text-4xl sm:text-5xl">Two plans. The prices the app charges.</h1>
        <p className="lede mt-5">
          This matches the catalog in organization settings. Individual is the account you get when
          you sign up. Team is the upgrade.
        </p>

        <div className="mt-10 grid gap-5 lg:grid-cols-2">
          {plans.map((plan) => (
            <article
              key={plan.name}
              className={`surface-card p-6 sm:p-8 ${plan.featured ? 'ring-2 ring-forest' : ''}`}
            >
              {plan.featured ? (
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-forest">Paid plan</p>
              ) : (
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">Free</p>
              )}
              <h2 className="mt-2 font-display text-3xl">{plan.name}</h2>
              <p className="mt-4 font-display text-4xl">{plan.price}</p>
              <p className="text-sm text-ink-soft">{plan.period}</p>
              <p className="mt-4 text-ink-soft">{plan.description}</p>
              <ul className="mt-6 space-y-2 text-sm">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex gap-2">
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-forest" />
                    {feature}
                  </li>
                ))}
              </ul>
              <a href={plan.href} className={`mt-8 ${plan.featured ? 'btn-premium' : 'btn-glass'}`}>
                {plan.cta}
              </a>
            </article>
          ))}
        </div>

        <div className="mt-14 overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
            <caption className="mb-3 text-left font-display text-2xl text-ink">Side by side</caption>
            <thead>
              <tr className="border-b border-line text-ink-soft">
                <th scope="col" className="py-3 pr-4 font-medium"> </th>
                <th scope="col" className="py-3 pr-4 font-medium">Individual</th>
                <th scope="col" className="py-3 font-medium">Team</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name} className="border-b border-line">
                  <th scope="row" className="py-3 pr-4 font-medium text-ink">{row.name}</th>
                  <td className="py-3 pr-4 text-ink-soft">{row.individual}</td>
                  <td className="py-3 text-ink-soft">{row.team}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-14 max-w-3xl">
          <h2 className="font-display text-3xl">Questions</h2>
          <div className="mt-4 divide-y divide-line border-y border-line">
            {faqs.map((faq, index) => {
              const expanded = open === index;
              return (
                <div key={faq.q}>
                  <button
                    type="button"
                    className="flex min-h-11 w-full items-center justify-between gap-4 py-4 text-left font-semibold"
                    aria-expanded={expanded}
                    onClick={() => setOpen(expanded ? null : index)}
                  >
                    {faq.q}
                    <span aria-hidden className="text-forest">{expanded ? '–' : '+'}</span>
                  </button>
                  {expanded && <p className="pb-4 text-ink-soft">{faq.a}</p>}
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-sm text-ink-soft">
            Something the table does not answer? <Link className="font-semibold text-forest" to="/contact">Write to us</Link> or
            email team@synqtag.com.
          </p>
        </div>
      </div>
    </div>
  );
};
